import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { getAuthenticatedHttpClient, getSiteConfig } from '@openedx/frontend-base';

export interface CourseTab { slug: string, title: string, url: string }
export interface CourseMetadata { courseId: string, title: string, tabs: CourseTab[] }

// One request per course: the course banner (header) and the course menu
// (page) of the instructor dashboard share it.
const pending = new Map<string, Promise<CourseMetadata>>();

function fetchMetadata(courseId: string): Promise<CourseMetadata> {
  if (!pending.has(courseId)) {
    const url = `${getSiteConfig().lmsBaseUrl}/api/course_home/course_metadata/${encodeURIComponent(courseId)}`;
    pending.set(courseId, getAuthenticatedHttpClient().get(url).then(({ data }) => ({
      courseId,
      title: data.title || '',
      tabs: (data.tabs || []).map((tab: any) => ({
        slug: tab.tab_id === 'courseware' ? 'outline' : tab.tab_id, title: tab.title, url: tab.url,
      })),
    })));
  }
  return pending.get(courseId)!;
}

// The course of the current route (/instructor-dashboard/:courseId/...), or
// null on pages without one and until it is loaded.
export function useCourseMetadata(): CourseMetadata | null {
  const { courseId = '' } = useParams();
  const [meta, setMeta] = useState<CourseMetadata | null>(null);
  useEffect(() => {
    let alive = true;
    setMeta(null);
    if (courseId) {
      fetchMetadata(courseId).then(m => { if (alive) { setMeta(m); } }).catch(() => {});
    }
    return () => { alive = false; };
  }, [courseId]);
  return meta;
}
